import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { api } from '../../lib/api';
import { useSEO } from '../../lib/seo';

export const AdminLogin = () => {
  useSEO({ title: 'Admin Login — CapableBee' });
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/admin/login', form);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-navy outline-none transition-all focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/25';

  return (
    <main className="flex min-h-screen items-center justify-center bg-brand-iceSoft px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm rounded-3xl border border-brand-line bg-white p-8 shadow-lift">
        <img src="/brand/logo.png" alt="CapableBee — AI Voice Assistant" className="h-9 w-auto" />
        <h1 className="mt-6 text-xl font-extrabold text-brand-navy">Admin Login</h1>
        <p className="mt-1 text-sm text-slate-500">Contact submissions dashboard</p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
          <div>
            <label htmlFor="al-email" className="mb-1.5 block text-sm font-bold text-brand-navy">
              Email
            </label>
            <input
              id="al-email"
              type="email"
              required
              autoComplete="username"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
              placeholder="admin@capablebee.com"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label htmlFor="al-password" className="mb-1.5 block text-sm font-bold text-brand-navy">
              Password
            </label>
            <input
              id="al-password"
              type="password"
              required
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className={inputCls}
              placeholder="••••••••••"
              data-testid="admin-login-password"
            />
          </div>

          {error && (
            <p role="alert" className="text-sm font-semibold text-red-500" data-testid="admin-login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-navy px-6 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-lift disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" /> Sign In
              </>
            )}
          </button>
        </form>

        <Link
          to="/"
          className="mt-6 block text-center text-xs font-semibold text-slate-400 transition-colors hover:text-brand-skyDeep"
        >
          ← Back to website
        </Link>
      </div>
    </main>
  );
};
